import type { FuseThemesType } from "@fuse/core/FuseSettings/FuseSettings";
import { DEFAULT_FONT_SANS } from "@/configs/system";
import {
	buildButtonStyleOverrides,
	buildButtonVariants,
	buildChipOverrides,
	buildChipVariants,
	buildIconButtonOverrides,
	type ButtonStrategyMap,
} from "./componentVariants";

/**
 * Design System SPACE — Configuracao centralizada de cores e tema.
 *
 * Modo unico (light). Todas as cores do sistema estao definidas aqui.
 * O Tailwind herda automaticamente via CSS vars do MUI (ver index.css @theme).
 */

/* ── Tokens de cor ─────────────────────────────────────────── */
const colors = {
	navy: "#003770",
	navyMid: "#1a4f8a",
	blueLt: "#85a3c2",
	purple: "#504a99",
	purple2: "#514b9a",
	purple3: "#864f9a",
	teal: "#76b0bf",
	sky: "#67a0d6",
	pink: "#e6215a",
} as const;

const fonts = {
	sans: `var(--font-sans, ${DEFAULT_FONT_SANS})`,
	mono: "var(--font-mono)",
} as const;

const surface = {
	bg: "#f0f3f8",
	paper: "#ffffff",
	secondary: "#f7f9fc",
	border: "#dde3ed",
	borderStrong: "#c4cfe0",
} as const;

const text = {
	primary: "#0d1f35",
	secondary: "#4a5a6e",
	disabled: "#8a9ab0",
} as const;

const gradients = {
	brand: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.purple} 100%)`,
	accent: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.sky} 100%)`,
	cta: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.navy} 100%)`,
} as const;

const shadows = {
	sm: "0 1px 4px rgba(0, 55, 112, 0.06)",
	md: "0 2px 12px rgba(0, 55, 112, 0.08)",
	lg: "0 4px 24px rgba(0, 55, 112, 0.12)",
	brand: "0 4px 16px rgba(80, 74, 153, 0.3)",
} as const;

const grey = {
	50: "#F8FAFC",
	100: "#F1F5F9",
	200: "#E2E8F0",
	300: "#CBD5E1",
	400: "#94A3B8",
	500: "#64748B",
	600: "#475569",
	700: "#334155",
	800: "#1E293B",
	900: "#003770",
	A100: "#F1F5F9",
	A200: "#E2E8F0",
	A400: "#94A3B8",
	A700: "#334155",
} as const;

const typography = {
	fontFamily: fonts.sans,
	fontSize: 13,
	fontWeightLight: 300,
	fontWeightRegular: 400,
	fontWeightMedium: 500,
	fontWeightBold: 700,
	h1: { fontSize: "2rem", fontWeight: 700, lineHeight: 1.2 },
	h2: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2 },
	h3: { fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.25 },
	h4: { fontSize: "1rem", fontWeight: 700, lineHeight: 1.3 },
	h5: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.3 },
	h6: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.35 },
	body1: { fontSize: "0.875rem", lineHeight: 1.4 },
	body2: { fontSize: "0.8125rem", lineHeight: 1.4 },
	button: {
		fontSize: "0.75rem",
		fontWeight: 600,
		lineHeight: 1.2,
		letterSpacing: "0.01em",
		textTransform: "none" as const,
	},
} as const;

const shape = { borderRadius: 8 } as const;

export const spaceThemeTokens = {
	colors,
	surface,
	text,
	fonts,
	gradients,
	shadows,
	shape,
} as const;

/* ── Palette (formato MUI) ─────────────────────────────────── */
const palette = {
	mode: "light" as const,
	primary: { main: colors.navy, light: colors.navyMid, dark: "#002a57", contrastText: "#FFFFFF" },
	secondary: { main: colors.purple, light: "#6b65b3", dark: "#3d3875", contrastText: "#FFFFFF" },
	success: { main: "#047857", light: "#059669", dark: "#065F46", contrastText: "#FFFFFF" },
	info: { main: "#0E7490", light: "#0A7D9E", dark: "#155E75", contrastText: "#FFFFFF" },
	warning: { main: "#B45309", light: "#C05621", dark: "#7C2D12", contrastText: "#FFFFFF" },
	error: { main: colors.pink, light: "#eb4d7b", dark: "#b81a48", contrastText: "#FFFFFF" },
	favorite: { main: "#B83273", light: "#C4497D", dark: "#8A2758", contrastText: "#FFFFFF" },
	grey,
	text,
	background: { default: surface.bg, paper: surface.paper },
	divider: surface.border,
	action: {
		active: grey[600],
		hover: grey[100],
		selected: grey[200],
		disabled: grey[400],
		disabledBackground: grey[50],
		focus: grey[200],
	},
};

/* ── Cores semânticas para variantes de componentes ────────── */
const semanticColors = {
	primary: palette.primary,
	secondary: palette.secondary,
	success: palette.success,
	warning: palette.warning,
	error: palette.error,
	info: palette.info,
	favorite: palette.favorite,
} as const;

const buttonStrategyMap: ButtonStrategyMap = {
	primary: {
		contained: {
			kind: "solid",
			backgroundColor: palette.primary.main,
			hoverBackgroundColor: palette.primary.dark,
			boxShadow: shadows.sm,
		},
	},
	secondary: {
		contained: {
			kind: "gradient",
			from: colors.sky,
			to: colors.navy,
			hoverFilter: "saturate(1.12) brightness(0.92)",
			boxShadow: shadows.brand,
		},
	},
	error: {
		contained: {
			kind: "gradient",
			from: palette.error.light,
			to: palette.error.dark,
			hoverFilter: "saturate(1.08) brightness(0.93)",
			boxShadow: shadows.sm,
		},
	},
	warning: {
		contained: {
			kind: "gradient",
			from: palette.warning.light,
			to: palette.warning.dark,
			hoverFilter: "saturate(1.08) brightness(0.93)",
			boxShadow: shadows.sm,
		},
		outlined: {
			color: palette.warning.dark,
			hoverColor: palette.warning.dark,
			borderAlpha: 0.65,
			hoverAlpha: 0.12,
		},
		text: {
			color: palette.warning.dark,
			hoverColor: palette.warning.dark,
			hoverAlpha: 0.12,
		},
	},
	favorite: {
		contained: {
			kind: "gradient",
			from: palette.favorite.light,
			to: palette.favorite.dark,
			hoverFilter: "saturate(1.08) brightness(0.93)",
			boxShadow: shadows.sm,
		},
	},
};

const buttonVariants = buildButtonVariants(semanticColors, buttonStrategyMap);
const buttonStyleOverrides = buildButtonStyleOverrides(semanticColors, buttonStrategyMap);
const iconButtonOverrides = buildIconButtonOverrides(semanticColors);
const chipVariants = buildChipVariants(semanticColors);
const chipOverrides = buildChipOverrides(semanticColors);

const defaultFilledChipStyle = {
	backgroundColor: grey[300],
	color: colors.navy,
	border: `1px solid ${grey[300]}`,
	"&:hover": {
		backgroundColor: grey[400],
	},
	"& .MuiChip-deleteIcon": {
		color: "rgba(0, 55, 112, 0.55)",
		"&:hover": { color: colors.navy },
	},
} as const;

const snackbarVariantStyleOverrides = {
	".notistack-MuiContent.notistack-MuiContent-default": {
		"--space-snackbar-bg": "#F8FAFC",
		"--space-snackbar-accent": colors.navy,
		"--space-snackbar-border": surface.borderStrong,
		"--space-snackbar-icon-bg": "rgba(0, 55, 112, 0.08)",
	},
	".notistack-MuiContent.notistack-MuiContent-success": {
		"--space-snackbar-bg": "#F0FDF4",
		"--space-snackbar-accent": palette.success.main,
		"--space-snackbar-border": "rgba(4, 120, 87, 0.22)",
		"--space-snackbar-icon-bg": "rgba(4, 120, 87, 0.1)",
	},
	".notistack-MuiContent.notistack-MuiContent-error": {
		"--space-snackbar-bg": "#FEF2F2",
		"--space-snackbar-accent": palette.error.main,
		"--space-snackbar-border": "rgba(185, 28, 28, 0.24)",
		"--space-snackbar-icon-bg": "rgba(185, 28, 28, 0.1)",
	},
	".notistack-MuiContent.notistack-MuiContent-warning": {
		"--space-snackbar-bg": "#FFFBEB",
		"--space-snackbar-accent": palette.warning.main,
		"--space-snackbar-border": "rgba(180, 83, 9, 0.24)",
		"--space-snackbar-icon-bg": "rgba(180, 83, 9, 0.11)",
	},
	".notistack-MuiContent.notistack-MuiContent-info": {
		"--space-snackbar-bg": "#EFF6FF",
		"--space-snackbar-accent": palette.info.main,
		"--space-snackbar-border": "rgba(37, 99, 235, 0.22)",
		"--space-snackbar-icon-bg": "rgba(37, 99, 235, 0.1)",
	},
} as const;

/* ── Overrides de componentes MUI ──────────────────────────── */
const components = {
	MuiAlert: {
		styleOverrides: {
			root: {
				paddingBlock: "2px",
				fontSize: "var(--text-sm, 0.75rem)",
				lineHeight: "var(--text-sm--line-height, 1.733333)",
				alignItems: "center",
			},
			action: {
				paddingTop: 0,
				alignItems: "center",
				alignSelf: "center",
			},
		},
	},
	MuiCssBaseline: {
		styleOverrides: {
			":root": {
				"--space-gradient-brand": gradients.brand,
				"--space-gradient-accent": gradients.accent,
				"--space-gradient-cta": gradients.cta,
				"--space-shadow-sm": shadows.sm,
				"--space-shadow-md": shadows.md,
				"--space-shadow-lg": shadows.lg,
				"--space-font-mono": fonts.mono,
			},
			html: {
				WebkitFontSmoothing: "antialiased",
				MozOsxFontSmoothing: "grayscale",
			},
			body: {
				backgroundColor: surface.bg,
				color: text.primary,
				fontFamily: fonts.sans,
				fontSize: "0.875rem",
				lineHeight: 1.5,
			},
			"code, pre, kbd, samp": {
				fontFamily: fonts.mono,
			},
			".notistack-MuiContent.notistack-MuiContent": {
				"--space-snackbar-bg": grey[50],
				"--space-snackbar-accent": colors.navy,
				"--space-snackbar-border": surface.border,
				"--space-snackbar-icon-bg": "rgba(0, 55, 112, 0.08)",
				position: "relative",
				display: "grid !important",
				gridTemplateColumns: "minmax(0, 1fr) auto",
				columnGap: 12,
				alignItems: "center",
				flexWrap: "nowrap !important",
				boxSizing: "border-box",
				minWidth: "min(340px, calc(100vw - 32px))",
				maxWidth: "min(440px, calc(100vw - 32px))",
				minHeight: 52,
				padding: "10px 10px 10px 12px !important",
				backgroundColor: "var(--space-snackbar-bg) !important",
				backgroundImage: "none !important",
				border: "1px solid var(--space-snackbar-border) !important",
				borderLeft: "4px solid var(--space-snackbar-accent) !important",
				borderRadius: "8px !important",
				boxShadow: "0 14px 34px rgba(13, 31, 53, 0.16), 0 2px 8px rgba(13, 31, 53, 0.08) !important",
				color: `${text.primary} !important`,
				fontFamily: `${fonts.sans} !important`,
				fontSize: "0.8125rem !important",
				fontWeight: "600 !important",
				letterSpacing: "0 !important",
				lineHeight: "1.35 !important",
				overflow: "hidden",
				"& > div:first-of-type": {
					display: "flex",
					alignItems: "center",
					minWidth: 0,
					padding: "0 !important",
					overflowWrap: "anywhere",
				},
				"& > div:nth-of-type(2)": {
					alignSelf: "center",
					justifySelf: "end",
					marginLeft: "0 !important",
					marginRight: "-2px !important",
					paddingLeft: "0 !important",
				},
				"& .app-snackbar-icon": {
					width: 28,
					height: 28,
					marginRight: 10,
					borderRadius: 999,
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					backgroundColor: "var(--space-snackbar-icon-bg)",
					boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.58)",
					color: "var(--space-snackbar-accent)",
				},
				"& .MuiIconButton-root": {
					width: 30,
					height: 30,
					padding: "0 !important",
					borderRadius: 8,
					color: `${text.secondary} !important`,
					opacity: "0.72 !important",
					transition: "background-color 0.12s ease, opacity 0.12s ease",
					"&:hover": {
						backgroundColor: "rgba(13, 31, 53, 0.07)",
						opacity: "1 !important",
					},
				},
			},
			...snackbarVariantStyleOverrides,
		},
	},
	MuiButton: {
		defaultProps: {
			size: "extraSmall" as const,
			disableElevation: true,
		},
		variants: [...(buttonVariants.variants ?? [])],
		styleOverrides: {
			root: {
				borderRadius: 8,
				fontWeight: 600,
				textTransform: "none",
				transition: "all 0.12s ease",
				"&.Mui-disabled": {
					color: text.disabled,
				},
			},
			sizeSmall: {
				minHeight: 28,
				padding: "5px 12px",
				fontSize: "0.6875rem",
				"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
					fontSize: "0.85rem",
					width: "0.85rem",
					height: "0.85rem",
				},
			},
			sizeMedium: {
				minHeight: 32,
				padding: "7px 16px",
				fontSize: "0.75rem",
				"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
					fontSize: "0.95rem",
					width: "0.95rem",
					height: "0.95rem",
				},
			},
			sizeLarge: {
				minHeight: 38,
				padding: "9px 20px",
				fontSize: "0.8125rem",
				"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
					fontSize: "1.05rem",
					width: "1.05rem",
					height: "1.05rem",
				},
			},
			...(buttonStyleOverrides ?? {}),
		},
	},
	MuiIconButton: {
		defaultProps: { size: "small" as const },
		styleOverrides: {
			root: {
				borderRadius: 8,
				transition: "all 0.12s ease",
			},
			...(iconButtonOverrides.styleOverrides ?? {}),
		},
	},
	MuiPaper: {
		styleOverrides: {
			root: {
				backgroundImage: "none",
			},
			elevation1: {
				boxShadow: shadows.sm,
				border: `1px solid ${surface.border}`,
			},
			rounded: {
				borderRadius: 12,
			},
		},
	},
	MuiCard: {
		styleOverrides: {
			root: {
				borderRadius: 12,
				border: `1px solid ${surface.border}`,
				boxShadow: shadows.sm,
			},
		},
	},
	MuiChip: {
		variants: [
			...(chipVariants.variants ?? []),
			{
				props: { variant: "filled" as const, color: "default" as const },
				style: defaultFilledChipStyle,
			},
		],
		styleOverrides: {
			root: {
				borderRadius: 9999,
				fontWeight: 600,
			},
			...(chipOverrides.styleOverrides ?? {}),
		},
	},
	MuiTableContainer: {
		styleOverrides: {
			root: {
				borderRadius: 12,
				border: `1px solid ${surface.border}`,
				boxShadow: shadows.md,
			},
		},
	},
	MuiTableHead: {
		styleOverrides: {
			root: {
				background: gradients.brand,
			},
		},
	},
	MuiTableRow: {
		styleOverrides: {
			root: {
				"&:nth-of-type(even):not(.MuiTableRow-head)": {
					backgroundColor: surface.secondary,
				},
				"&:hover:not(.MuiTableRow-head)": {
					backgroundColor: "rgba(80, 74, 153, 0.05)",
				},
			},
		},
	},
	MuiTableCell: {
		styleOverrides: {
			root: {
				borderColor: surface.border,
				padding: "10px 12px",
				fontSize: "0.75rem",
			},
			head: {
				color: "rgba(255,255,255,0.85)",
				fontWeight: 700,
				fontSize: "0.6875rem",
				letterSpacing: "0.07em",
				textTransform: "uppercase",
				borderBottomColor: "rgba(255,255,255,0.2)",
			},
			body: {
				color: text.primary,
			},
			footer: {
				backgroundColor: "rgba(80, 74, 153, 0.08)",
				color: colors.navy,
				fontWeight: 700,
			},
		},
	},
	MuiPaginationItem: {
		styleOverrides: {
			root: {
				borderRadius: 8,
				fontWeight: 600,
			},
			outlined: {
				borderColor: surface.border,
			},
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				backgroundColor: surface.paper,
				borderRadius: 8,
				"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: grey[400] },
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderColor: palette.secondary.main,
					boxShadow: "0 0 0 3px rgba(80, 74, 153, 0.1)",
				},
			},
		},
	},
};

/* ── Palette para navbar / toolbar (fundo escuro, modo light) ─ */
const navbarPalette = {
	...palette,
	text: {
		primary: "#F8FAFC",
		secondary: "#94A3B8",
		disabled: "#64748B",
	},
	background: { default: grey[900], paper: grey[800] },
	divider: grey[700],
	action: {
		active: grey[300],
		hover: "rgba(255,255,255,0.08)",
		selected: "rgba(255,255,255,0.16)",
		disabled: grey[600],
		disabledBackground: grey[800],
		focus: "rgba(255,255,255,0.12)",
	},
};

/* ── Exports para Fuse ─────────────────────────────────────── */
export const lightPaletteText = text;

export const themesConfig: FuseThemesType = {
	default: { palette, typography, shape, components },
	navbar: { palette: navbarPalette },
	toolbar: { palette: navbarPalette },
};

export default themesConfig;
